import { CSSProperties } from 'react'
import styled, { css, DefaultTheme, RuleSet } from 'styled-components'

import { Button } from '@/components/ui/button'
import { media } from '@/theme/common'
import { Spacings } from '@/theme/defaults'
import { withBorder } from '@/theme/utils'

import { FieldState, TextInputLabelPosition, TextInputSize } from './text-input.types'

export const getInputState = ({
  isDisabled,
  isReadOnly,
  hasError,
}: {
  isDisabled: boolean
  isReadOnly: boolean
  hasError: boolean
}): FieldState => {
  if (isDisabled) return 'disabled'
  if (isReadOnly) return 'readOnly'
  if (hasError) return 'error'
  return 'default'
}

// Note: these maps describe the shared field-control metrics (gap between the
// label/field/footer rows, and the value text size/line-height per size). They
// are exported so sibling field controls (e.g. `Select`) can mirror `TextInput`
// exactly instead of re-deriving the same numbers.
export const SIZE_TO_GAP_MAP: Record<TextInputSize, keyof Spacings> = {
  large: 12,
  medium: 8,
  small: 4,
}

export const INPUT_FONT_SIZE_MAP: Record<TextInputSize, string> = {
  large: '1.6rem',
  medium: '1.4rem',
  small: '1.2rem',
}

export const INPUT_LINE_HEIGHT_MAP: Record<TextInputSize, string> = {
  large: '2.4rem',
  medium: '2rem',
  small: '1.6rem',
}

export const Container = styled.div<{
  $labelPosition: TextInputLabelPosition
  $size: TextInputSize
}>(({ theme, $labelPosition, $size }) => ({
  alignItems: $labelPosition === 'left' ? 'baseline' : 'stretch',
  display: 'flex',
  flexDirection: $labelPosition === 'left' ? 'row' : 'column',
  gap: $labelPosition === 'left' ? theme.space['16'] : theme.space[SIZE_TO_GAP_MAP[$size]],
  position: 'relative',
  width: '100%',
}))

// Note: holds the input row and the footer (helper/error + counter) as one column.
export const Field = styled.div<{ $size: TextInputSize }>(({ theme, $size }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[SIZE_TO_GAP_MAP[$size]],
    width: '100%',
  }
})

// Note: the shared bordered-box visuals for field-like controls. Parameterized by
// `state` + `size` so any control can render the *identical* box — border,
// background, padding, min-height, hover/focus transitions and icon sizing.
// `interactiveCursor` is the cursor used in the interactive (`default`/`error`)
// states: a text field wants `text`, a button-driven control (e.g. `Select`)
// wants `pointer`. This is the single source of truth for the field box look.
export const getFieldBoxStyles = ({
  theme,
  state,
  size,
  interactiveCursor = 'text',
}: {
  theme: DefaultTheme
  state: FieldState
  size: TextInputSize
  interactiveCursor?: CSSProperties['cursor']
}): RuleSet => {
  const backgroundMap: Record<FieldState, CSSProperties['background']> = {
    default: 'transparent',
    disabled: theme.colors.gray200,
    error: theme.colors.red100,
    readOnly: 'transparent',
  }

  const borderColorMap: Record<FieldState, CSSProperties['borderColor']> = {
    default: theme.colors.gray500,
    disabled: theme.colors.gray200,
    error: theme.colors.red200,
    readOnly: 'transparent',
  }

  const readOnlyPaddingMap: Record<TextInputSize, CSSProperties['padding']> = {
    large: `${theme.space['4']} 0`,
    medium: `${theme.space['4']} 0`,
    small: `${theme.space['4']} 0`,
  }

  const otherPaddingMap: Record<TextInputSize, CSSProperties['padding']> = {
    large: `${withBorder(theme, 4)} ${withBorder(theme, 16)}`,
    medium: `${withBorder(theme, 4)} ${withBorder(theme, 12)}`,
    small: `${withBorder(theme, 4)} ${withBorder(theme, 12)}`,
  }

  const cursorMap: Record<FieldState, CSSProperties['cursor']> = {
    default: interactiveCursor,
    disabled: 'not-allowed',
    error: interactiveCursor,
    readOnly: 'default',
  }

  const minHeightMap: Record<TextInputSize, CSSProperties['minHeight']> = {
    large: `4.8rem`,
    medium: `4rem`,
    small: `3.2rem`,
  }

  const sizeStyles: Record<TextInputSize, RuleSet> = {
    large: css`
      svg {
        width: 2.4rem;
      }
    `,
    medium: css`
      svg {
        width: 2rem;
      }
    `,
    small: css`
      svg {
        width: 1.6rem;
      }
    `,
  }

  const isInteractive = state === 'default' || state === 'error'

  return css`
    position: relative;
    display: flex;
    align-items: center;
    background: ${backgroundMap[state]};
    border: ${state === 'readOnly' ? 0 : theme.border.size['1']} solid ${borderColorMap[state]};
    border-radius: ${theme.border.radius['4']};
    cursor: ${cursorMap[state]};
    min-height: ${minHeightMap[size]};
    padding: ${state === 'readOnly' ? readOnlyPaddingMap[size] : otherPaddingMap[size]};
    width: 100%;
    transition: border-color ${theme.transition.duration.sm} ease;

    ${
      state !== 'readOnly' &&
      css`
        ${media.down('sm')} {
          padding-left: ${theme.space['4']};
          padding-right: ${theme.space['4']};
        }
      `
    }

    svg {
      aspect-ratio: 1 / 1;
    }

    & > *:not(${ActionsWrapper}) {
      cursor: ${cursorMap[state]};
    }

    // Note: hover only applies on devices that actually support hovering (mouse).
    ${
      isInteractive &&
      css`
      &:focus-within {
        border-color: ${state === 'error' ? theme.colors.red500 : theme.colors.primary800};
      }

      @media (hover: hover) {
        &:hover:not(:focus-within) {
          border-color: ${state === 'error' ? theme.colors.red300 : theme.colors.primary400};
        }
      }
    `
    }

    ${sizeStyles[size]}
  `
}

// NOTE: Usage of double `styled.label()` is intentional. It's allowed to have multiple labels for input,
// but we need that to redirect events such as clicks on wrapper/icons inside it that are not input
// to the actual input.
// Aria accessibility for free - just keep in mind to do `preventDefault()` or `stopPropagation` on nested buttons
export const InputWrapper = styled.label<{
  $size: TextInputSize
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly: boolean
}>(({ theme, $size, $hasError, $isDisabled, $isReadOnly }) =>
  getFieldBoxStyles({
    size: $size,
    state: getInputState({
      hasError: $hasError,
      isDisabled: $isDisabled,
      isReadOnly: $isReadOnly,
    }),
    theme,
  }),
)

// Note: thin separator between a leading/trailing icon and the text.
export const Divider = styled.div<{
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly?: boolean
}>(({ theme, $hasError, $isDisabled, $isReadOnly }) => {
  const state = getInputState({
    hasError: $hasError,
    isDisabled: $isDisabled,
    isReadOnly: $isReadOnly ?? false,
  })

  const backgroundMap: Record<FieldState, CSSProperties['background']> = {
    default: theme.colors.gray500,
    disabled: theme.colors.gray500,
    error: theme.colors.red200,
    readOnly: theme.colors.gray500,
  }

  return {
    background: backgroundMap[state],
    flexShrink: 0,
    height: '1.6rem',
    width: theme.border.size['1'],
  }
})

// Note: holds the leading icon (or a custom `leadingSlot`) + divider. When `$size`
// is set, the slot inherits the input's text size/line-height so custom text
// content (e.g., a country dial code) lines up with the input value.
export const LeadingSlot = styled.div<{
  $size?: TextInputSize
  $hasError?: boolean
  $isDisabled: boolean
  $isReadOnly?: boolean
}>(({ theme, $size, $hasError, $isDisabled, $isReadOnly }) => {
  const state = getInputState({
    hasError: $hasError ?? false,
    isDisabled: $isDisabled,
    isReadOnly: $isReadOnly ?? false,
  })

  const colorMap: Record<FieldState, CSSProperties['color']> = {
    default: theme.colors.primary800,
    disabled: theme.colors.primary500,
    error: theme.colors.red500,
    readOnly: theme.colors.primary800,
  }

  return {
    alignItems: 'center',
    color: colorMap[state],
    display: 'flex',
    fontSize: $size ? INPUT_FONT_SIZE_MAP[$size] : undefined,
    gap: theme.space['8'],
    lineHeight: $size ? INPUT_LINE_HEIGHT_MAP[$size] : undefined,
    paddingRight: theme.space['8'],

    [media.down('sm')]: {
      gap: theme.space['4'],
      paddingRight: theme.space['4'],
    },
  }
})

export const ActionsWrapper = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['4'],
}))

// Note: the clear/restore actions reuse the shared `Button` with the `ghost`
// (or `ghost-error`) appearance for its hover/focus/active logic and colors.
// `Button` is rendered with `size="unset"`, so the only field-specific tweak is
// the trailing-action padding scale (smaller than `Button`'s own size padding).
// The icon size still comes from the field box's `svg` rule on `InputWrapper`.
export const InputActionButton = styled(Button)<{ $size: TextInputSize }>(({ theme, $size }) => {
  const paddingMap: Record<TextInputSize, CSSProperties['padding']> = {
    large: `${theme.space['8']}`,
    medium: `${theme.space['6']}`,
    small: `${theme.space['4']}`,
  }

  return {
    flexShrink: 0,
    padding: `${paddingMap[$size]} !important`,
  }
})

export const Input = styled.input<{
  $hasError: boolean
  $size: TextInputSize
}>(({ theme, $hasError, $size }) => {
  return css`
    background: transparent;
    border: none;
    color: ${theme.colors.primary800};
    cursor: inherit;
    flex: 1;
    font-family: inherit;
    font-size: ${INPUT_FONT_SIZE_MAP[$size]};
    height: ${INPUT_LINE_HEIGHT_MAP[$size]};
    line-height: ${INPUT_LINE_HEIGHT_MAP[$size]};
    outline: none;
    padding: 0;
    margin: 0;
    // NOTE: "min-width: 0;" fixes case when "styled.input" tag takes space out of bounds of it's wrapper
    // during small "TextInput" sizes
    min-width: 0;
    text-align: left;

    &::placeholder {
      color: ${theme.colors.primary500};
    }

    ${
      $hasError &&
      css`
      color: ${theme.colors.red500};

      &::placeholder {
        color: ${theme.colors.red400};
      }
    `
    }

    // NOTE: by our design we have pretty UI-less styles for "read-only" state,
    // so, to make it ARIA compliant -> adding browser default visuals
    &:read-only:focus-visible {
      outline: -webkit-focus-ring-color auto ${theme.border.size['1']};
    }

    &:disabled {
      color: ${theme.colors.primary500};

      &::placeholder {
        color: ${theme.colors.primary400};
      }
    }
  `
})

// Note: footer holds the helper/error message and the optional character counter on one row.
export const Footer = styled.div<{ $size: TextInputSize }>(({ theme, $size }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  fontSize: INPUT_FONT_SIZE_MAP[$size],
  gap: theme.space['8'],
  lineHeight: INPUT_LINE_HEIGHT_MAP[$size],
  minHeight: INPUT_LINE_HEIGHT_MAP[$size],
}))

export const Message = styled.span<{ $hasError: boolean }>(({ theme, $hasError }) => ({
  color: $hasError ? theme.colors.red500 : theme.colors.primary600,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

export const Counter = styled.span<{ $isOverLimit: boolean }>(({ theme, $isOverLimit }) => ({
  color: $isOverLimit ? theme.colors.red500 : theme.colors.black500,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  marginLeft: 'auto',
}))

// NOTE: Preserve same number of digits for current count, so visually left and right columns have the same width
export const CurrentCount = styled.span<{ $digits: number }>(({ $digits }) => ({
  display: 'inline-block;',
  fontVariantNumeric: 'tabular-nums',
  minWidth: `${$digits}ch`,
  textAlign: 'right',
}))
